import { useEffect, useState } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../firebase/firebase";
import { useAuth } from "../../context/AuthContext";

function EvaluatorProfile() {
  const { user, profile, loading } = useAuth();

  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    fullName: "",
    title: "",
    organization: "",
    website: "",
  });

  // Local preview states (instant UI update)
  const [profilePreview, setProfilePreview] = useState(null);

  // Logo upload state
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState("");
  const [logoSuccess, setLogoSuccess] = useState("");
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);

  // Collapsible section state
  const [openProfile, setOpenProfile] = useState(true);
  const [openLogo, setOpenLogo] = useState(false);

  useEffect(() => {
    if (!profile) return;

    setEditData({
      fullName: profile.fullName || "",
      title: profile.title || "",
      organization: profile.organization || "",
      website: profile.website || "",
    });

    setProfilePreview(profile);

    if (profile.evaluatorLogoUrl) {
      setLogoPreviewUrl(profile.evaluatorLogoUrl);
    }
  }, [profile]);

  const saveProfileEdits = async () => {
    try {
      await updateDoc(doc(db, "users", user.uid), { ...editData });

      // 🔥 Instant UI update
      setProfilePreview((prev) => ({
        ...prev,
        ...editData,
      }));

      setIsEditing(false);
    } catch {
      alert("Failed to save profile.");
    }
  };

  /* ================= LOGO UPLOAD ================= */
  const handleLogoUpload = async (file) => {
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setLogoError("Please upload a valid image file.");
      return;
    }

    const MAX_SIZE = 1 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      setLogoError("Logo must be smaller than 1 MB.");
      return;
    }

    try {
      setLogoError("");
      setLogoSuccess("");
      setLogoUploading(true);

      const storageRef = ref(
        storage,
        `evaluator-logos/${user.uid}/logo`
      );
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      await updateDoc(doc(db, "users", user.uid), {
        evaluatorLogoUrl: url,
      });

      setLogoPreviewUrl(url);
      setLogoSuccess("Logo uploaded successfully.");
    } catch {
      setLogoError("Logo upload failed.");
    } finally {
      setLogoUploading(false);
    }
  };

  if (loading) return <p>Loading profile…</p>;
  if (!profile) return <p>No profile data available.</p>;

  const activeProfile = profilePreview || profile;
  const industries = activeProfile.industries || [];

  return (
    <div>
      <h1>Profile</h1>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "2fr 1fr",
          gap: 32,
          marginTop: 32,
          maxWidth: 960,
        }}
      >
        {/* ================= PROFILE CARD ================= */}
        <div
          className="card"
          style={{
            padding: 0,
            overflow: "hidden",
            transition: "all 0.3s ease",
            boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
            alignSelf: "start",
          }}
        >
          <div
            onClick={() => setOpenProfile((v) => !v)}
            style={{
              padding: 20,
              cursor: "pointer",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background:
                "linear-gradient(135deg, rgba(59,130,246,0.12), rgba(99,102,241,0.08))",
            }}
          >
            <h3 style={{ margin: 0 }}>Profile Information</h3>
            <span style={{ fontSize: 18 }}>
              {openProfile ? "−" : "+"}
            </span>
          </div>

          {openProfile && (
            <div style={{ padding: 24 }}>
              {isEditing ? (
                <>
                  <label>Full name</label>
                  <input
                    value={editData.fullName}
                    onChange={(e) =>
                      setEditData({ ...editData, fullName: e.target.value })
                    }
                    style={{ width: "100%", marginBottom: 12 }}
                  />

                  <label>Role / title</label>
                  <input
                    value={editData.title}
                    onChange={(e) =>
                      setEditData({ ...editData, title: e.target.value })
                    }
                    style={{ width: "100%", marginBottom: 12 }}
                  />

                  <label>Organization</label>
                  <input
                    value={editData.organization}
                    onChange={(e) =>
                      setEditData({
                        ...editData,
                        organization: e.target.value,
                      })
                    }
                    style={{ width: "100%", marginBottom: 12 }}
                  />

                  <label>Website / LinkedIn</label>
                  <input
                    value={editData.website}
                    onChange={(e) =>
                      setEditData({ ...editData, website: e.target.value })
                    }
                    style={{ width: "100%", marginBottom: 16 }}
                  />

                  <div style={{ display: "flex", gap: 10 }}>
                    <button className="btn-primary" onClick={saveProfileEdits}>
                      Save Changes
                    </button>
                    <button
                      className="btn-secondary"
                      onClick={() => setIsEditing(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p><strong>Name:</strong> {activeProfile.fullName}</p>
                  <p><strong>Role:</strong> {activeProfile.title}</p>
                  <p><strong>Organization:</strong> {activeProfile.organization}</p>

                  {activeProfile.website && (
                    <p>
                      <strong>Website:</strong>{" "}
                      <a href={activeProfile.website} target="_blank" rel="noreferrer">
                        {activeProfile.website}
                      </a>
                    </p>
                  )}

                  <div style={{ marginTop: 12 }}>
                    <strong>Industries:</strong>
                    <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                      {industries.map((ind) => (
                        <span
                          key={ind}
                          style={{
                            padding: "5px 12px",
                            background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
                            color: "#fff",
                            borderRadius: 999,
                            fontSize: 12,
                          }}
                        >
                          {ind}
                        </span>
                      ))}
                    </div>
                  </div>

                  <button
                    className="btn-secondary"
                    onClick={() => setIsEditing(true)}
                    style={{ marginTop: 16 }}
                  >
                    Edit Profile
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* ================= LOGO CARD ================= */}
        <div
          className="card"
          style={{
            padding: 0,
            overflow: "hidden",
            textAlign: "center",
            transition: "all 0.3s ease",
            boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
            alignSelf: "start",
          }}
        >
          <div
            onClick={() => setOpenLogo((v) => !v)}
            style={{
              padding: 20,
              cursor: "pointer",
              background:
                "linear-gradient(135deg, rgba(16,185,129,0.12), rgba(59,130,246,0.08))",
            }}
          >
            <h3 style={{ margin: 0 }}>Evaluator Logo</h3>
          </div>

          {openLogo && (
            <div style={{ padding: 24 }}>
              {logoPreviewUrl && (
                <div
                  style={{
                    height: 120,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 16,
                  }}
                >
                  <img
                    src={logoPreviewUrl}
                    alt="Evaluator logo"
                    style={{
                      maxHeight: "100%",
                      maxWidth: "100%",
                      objectFit: "contain",
                    }}
                  />
                </div>
              )}

              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleLogoUpload(e.target.files[0])}
              />

              {logoUploading && <p>Uploading logo…</p>}
              {logoSuccess && <p style={{ color: "#16a34a" }}>{logoSuccess}</p>}
              {logoError && <p style={{ color: "red" }}>{logoError}</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default EvaluatorProfile;
